import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Avatar,
  Button,
  Checkbox,
  Icon,
  IconButton,
  Input,
  Modal,
  PageBody,
  Pill,
  Select,
  SkeletonText,
  useToast,
} from "@astrum/ui";
import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORY_DESCRIPTION,
  NOTIFICATION_CATEGORY_LABEL,
  type ChannelPreference,
  type NotificationCategory,
} from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { useWorkspace } from "../lib/workspace.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { disablePush, enablePush, pushState } from "../lib/push.js";
import { enableNativePush, isNativeApp, nativePushPermission } from "../lib/native-app.js";
import type { NativePushPermission } from "../lib/native-app.js";
import type { PushState } from "../lib/push.js";
import {
  checkForUpdate,
  desktopPopupsEnabled,
  installUpdate,
  isAutostartEnabled,
  isDesktop,
  isStartMinimisedEnabled,
  openNotificationSettings,
  sendTestNotification,
  setAutostart,
  setDesktopPopupsEnabled,
  setStartMinimised,
  uploadFile,
  readUploadResponse,
  type AvailableUpdate,
} from "../lib/transport.js";
import { uploadFailure, uploadThrownMessage } from "../lib/upload-error.js";
import { AboutCard, WhatsNewCard } from "../components/AboutCards.js";
import { StatusSettings } from "../components/StatusSettings.js";
import "./profile.css";

/**
 * The signed-in person's own account.
 *
 * Two-factor enrolment and "sign out everywhere" both existed on the API from
 * Phase 2 and had no way in — this is the screen that gives them one. Nothing
 * here is decorative: every control performs a real, verified action.
 */

type SetupResponse = { setupId: string; secret: string; otpauthUrl: string };

const ROLE_LABEL: Record<string, string> = {
  admin: "Administrator",
  manager: "Manager",
  editor: "Editor",
  member: "Member",
  viewer: "Viewer",
};


/* ─────────────────────────── avatar upload ─────────────────────────── */

/**
 * The face, made changeable.
 *
 * `avatarUrl` was rendered from the start and never settable, so everyone was
 * a coloured circle. This is the one control that changes it: the avatar is
 * the button, because that is the thing being changed.
 */
function AvatarUpload({
  name,
  avatarUrl,
  onChanged,
}: {
  name: string;
  avatarUrl: string | null | undefined;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  const upload = useCallback(
    (file: File) => {
      // Refused here as well as by the server: no reason to send four
      // megabytes only to be told no.
      if (!file.type.startsWith("image/")) {
        toast("Choose an image file.", { tone: "danger" });
        return;
      }
      setBusy(true);
      const form = new FormData();
      form.append("file", file);
      // A raw fetch, matching how attachments upload: multipart needs the
      // browser to set its own Content-Type boundary, which the JSON client
      // would override.
      uploadFile("/auth/avatar", form)
        .then(async (response) => {
          const result = await readUploadResponse(response);
          if (!result.ok) throw uploadFailure(result);
          toast("Picture updated.");
          onChanged();
        })
        .catch((error: unknown) => {
          toast(uploadThrownMessage(error), { tone: "danger" });
        })
        .finally(() => setBusy(false));
    },
    [onChanged, toast],
  );

  const remove = useCallback(() => {
    setBusy(true);
    api
      .delete("/auth/avatar")
      .then(() => {
        toast("Back to initials.");
        onChanged();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not remove the picture.", { tone: "danger" });
      })
      .finally(() => setBusy(false));
  }, [onChanged, toast]);

  return (
    <div className="profile-avatar">
      <button
        type="button"
        className="profile-avatar__button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        aria-label={avatarUrl ? "Change your picture" : "Add a picture"}
      >
        <Avatar name={name} src={avatarUrl ?? undefined} size={64} />
        <span className="profile-avatar__overlay">
          <Icon name={busy ? "clock" : "edit"} size={16} />
        </span>
      </button>

      {avatarUrl && (
        <button type="button" className="profile-avatar__remove" onClick={remove} disabled={busy}>
          Remove
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        aria-label="Profile photo"
        className="profile-avatar__input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) upload(file);
          // Cleared so choosing the same file twice still fires onChange.
          event.target.value = "";
        }}
      />
    </div>
  );
}

/* ───────────────────────────────── name ────────────────────────────────── */

/**
 * Click the name to change it.
 *
 * Inline rather than a separate card: the name is right there in the heading,
 * and a person looking to change it will reach for the thing itself before
 * hunting for a settings section named after it.
 */
function NameEditor({ name, onChanged }: { name: string; onChanged: (name: string) => void }) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const begin = useCallback(() => {
    setValue(name);
    setEditing(true);
  }, [name]);

  const cancel = useCallback(() => setEditing(false), []);

  const save = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    api
      .put<{ displayName: string }>("/auth/profile", { displayName: trimmed })
      .then((res) => {
        onChanged(res.displayName);
        setEditing(false);
        toast("Name updated.", { tone: "ok" });
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not update your name.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  }, [value, name, onChanged, toast]);

  if (!editing) {
    return (
      <button type="button" className="profile-name" onClick={begin} aria-label="Change your name">
        <h1>{name}</h1>
        <Icon name="edit" size={15} className="profile-name__icon" />
      </button>
    );
  }

  return (
    <div className="profile-name profile-name--editing">
      <input
        ref={inputRef}
        className="profile-name__input"
        value={value}
        maxLength={120}
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") cancel();
        }}
      />
      <IconButton icon={<Icon name="check" size={14} />} label="Save name" size="sm" onClick={save} disabled={saving} />
      <IconButton icon={<Icon name="close" size={14} />} label="Cancel" size="sm" onClick={cancel} disabled={saving} />
    </div>
  );
}

/* \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 two-factor enrolment \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */

export function TwoFactorSetup({ onEnabled, onClose }: { onEnabled: () => void; onClose: () => void }) {
  const { toast } = useToast();
  const [setup, setSetup] = useState<SetupResponse | undefined>(undefined);
  const [code, setCode] = useState("");
  const [starting, setStarting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const begin = useCallback(() => {
    setStarting(true);
    api
      .post<SetupResponse>("/auth/two-factor/setup")
      .then(setSetup)
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not start enrolment.", { tone: "danger" });
      })
      .finally(() => setStarting(false));
  }, [toast]);

  const confirm = useCallback(() => {
    if (!setup || code.length !== 6) return;
    setVerifying(true);
    api
      .post("/auth/two-factor/enable", { setupId: setup.setupId, code })
      .then(() => {
        toast("Two-factor authentication is on.");
        onEnabled();
        onClose();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "That code was not accepted.", { tone: "danger" });
      })
      .finally(() => setVerifying(false));
  }, [setup, code, onEnabled, onClose, toast]);

  if (!setup) {
    return (
      <div className="profile__dialog">
        <p className="profile__muted">
          You will need an authenticator app — 1Password, Authy, Google Authenticator, or the one built into your
          password manager. After this, signing in asks for a six-digit code as well as your password.
        </p>
        <div className="profile__dialog-actions">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={starting} onClick={begin}>
            Start setup
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile__dialog">
      <ol className="profile__steps">
        <li>
          <strong>Add this key to your authenticator</strong>
          {/* The raw secret rather than a QR code: rendering one needs a
              library, and every authenticator accepts a typed key. */}
          <code className="profile__secret">{setup.secret}</code>
          <span className="profile__muted">
            Account: <strong>Astrum Connect</strong>
          </span>
        </li>
        <li>
          <strong>Enter the six-digit code it shows</strong>
          <Input
            label="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            autoFocus
          />
        </li>
      </ol>

      <div className="profile__dialog-actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={verifying} disabled={code.length !== 6} onClick={confirm}>
          Turn on
        </Button>
      </div>
    </div>
  );
}

/* ───────────────────────── desktop app settings ───────────────────────── */

/**
 * Settings that only exist in the installed app.
 *
 * Rendered nowhere else — a browser tab cannot start with the computer, and a
 * toggle that silently does nothing is worse than an absent one.
 */
/**
 * The desktop app's counterpart to the browser push card above.
 *
 * Two switches sit in the way of a popup: this app's own (whether it asks the
 * OS at all) and the OS's (whether it lets the app through). Only the first is
 * ours to flip, so the second gets a button straight to the right settings
 * page — and a test, since the OS never tells an app it has been muted and the
 * only honest way to find out is to try.
 */
function DesktopNotificationSettings() {
  const { toast } = useToast();
  const [popups, setPopups] = useState(() => desktopPopupsEnabled());
  const [testing, setTesting] = useState(false);

  const toggle = useCallback((next: boolean) => {
    setDesktopPopupsEnabled(next);
    setPopups(next);
  }, []);

  const test = useCallback(() => {
    setTesting(true);
    sendTestNotification()
      .then(() =>
        toast("Test sent. If nothing appeared, allow Astrum Connect in your notification settings."),
      )
      .catch(() => toast("Your system refused the notification. Check its notification settings.", { tone: "danger" }))
      .finally(() => setTesting(false));
  }, [toast]);

  const openSettings = useCallback(() => {
    openNotificationSettings().catch(() =>
      toast("Could not open notification settings. Open them from your system settings instead.", { tone: "danger" }),
    );
  }, [toast]);

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="bell" size={15} />
          Desktop notifications
        </h2>
      </div>
      <p className="profile__muted">
        Popups for new messages and work, shown while the app is open or running in the tray. Nothing pops up for the
        conversation you are already looking at.
      </p>

      <label className="profile__toggle-row">
        <Checkbox
          checked={popups}
          onChange={(e) => toggle(e.target.checked)}
          aria-label="Show notification popups"
        />
        <span>
          <span className="profile__notif-label">Show popups</span>
          <span className="profile__notif-desc">
            Turn off to keep notifications in the bell only. Your system can also block them — use the buttons below
            to check.
          </span>
        </span>
      </label>

      <div className="profile__card-actions">
        <Button variant="secondary" loading={testing} disabled={!popups} onClick={test}>
          Send a test notification
        </Button>
        <Button variant="secondary" onClick={openSettings}>
          Open system notification settings
        </Button>
      </div>
    </section>
  );
}

/**
 * The phone apps' counterpart to the browser push card.
 *
 * Pushes come through Apple and Google rather than the browser, so the only
 * switch is the phone's own permission. Once someone has said no, neither
 * platform lets the app ask again, so the card points them to Settings.
 */
function PhoneNotificationSettings() {
  const { toast } = useToast();
  const [permission, setPermission] = useState<NativePushPermission | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void nativePushPermission()
      .then(setPermission)
      .catch(() => undefined);
  }, []);

  const turnOn = useCallback(() => {
    setBusy(true);
    enableNativePush()
      .then(setPermission)
      .catch(() => toast("Could not turn on notifications.", { tone: "danger" }))
      .finally(() => setBusy(false));
  }, [toast]);

  const test = useCallback(() => {
    setBusy(true);
    api
      .post<{ sent: number }>("/push/devices/test")
      .then(({ sent }) =>
        toast(
          sent > 0
            ? "Test sent. It should arrive in a few seconds."
            : "This phone is not registered yet. Close and reopen the app, then try again.",
          sent > 0 ? {} : { tone: "danger" },
        ),
      )
      .catch((error: unknown) =>
        toast(error instanceof ApiError ? error.message : "Could not send a test notification.", { tone: "danger" }),
      )
      .finally(() => setBusy(false));
  }, [toast]);

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="bell" size={15} />
          Phone notifications
        </h2>
      </div>
      <p className="profile__muted">
        {permission === "denied"
          ? "Notifications are turned off for Astrum Connect. The app cannot ask again: turn them on in your phone's Settings, under Notifications, then come back here."
          : permission === "granted"
            ? "On for this phone. You will be notified about messages and work even when the app is closed. Quiet hours and Do not disturb still apply."
            : "Be told when someone messages you or assigns you work, even when the app is closed."}
      </p>
      {permission !== null && permission !== "denied" && (
        <div className="profile__card-actions">
          {permission === "granted" ? (
            <Button variant="secondary" loading={busy} onClick={test}>
              Send a test notification
            </Button>
          ) : (
            <Button variant="primary" loading={busy} onClick={turnOn}>
              Turn on notifications
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

function DesktopSettings() {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  // Null also when the installed app predates the setting; it is then hidden.
  const [minimised, setMinimised] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void isAutostartEnabled().then((value) => {
      if (!cancelled) setEnabled(value);
    });
    void isStartMinimisedEnabled().then((value) => {
      if (!cancelled) setMinimised(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleMinimised = useCallback(
    (next: boolean) => {
      setMinimised(next);
      void setStartMinimised(next).catch(() => {
        toast("Could not change that startup setting.", { tone: "danger" });
        void isStartMinimisedEnabled().then(setMinimised);
      });
    },
    [toast],
  );

  const toggle = useCallback(
    (next: boolean) => {
      // Optimistic, then corrected: the OS is the authority on whether this
      // actually took, and a checkbox that lies about a startup item is worse
      // than one that flickers.
      setEnabled(next);
      void setAutostart(next)
        .then(() => isAutostartEnabled())
        .then((actual) => setEnabled(actual))
        .catch(() => {
          toast("Could not change that startup setting.", { tone: "danger" });
          void isAutostartEnabled().then(setEnabled);
        });
    },
    [toast],
  );

  if (enabled === null) return null;

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="settings" size={15} />
          Desktop app
        </h2>
      </div>
      <p className="profile__muted">
        Closing the window keeps Astrum Connect running so messages still reach you. Quit from the tray icon to stop
        it entirely.
      </p>

      <label className="profile__toggle-row">
        <Checkbox
          checked={enabled}
          onChange={(e) => toggle(e.target.checked)}
          aria-label="Start Astrum Connect when I sign in"
        />
        <span>
          <span className="profile__notif-label">Start when I sign in</span>
          <span className="profile__notif-desc">So notifications arrive from the start.</span>
        </span>
      </label>

      {enabled && minimised !== null && (
        <label className="profile__toggle-row">
          <Checkbox
            checked={minimised}
            onChange={(e) => toggleMinimised(e.target.checked)}
            aria-label="Start minimised to the tray"
          />
          <span>
            <span className="profile__notif-label">Start minimised</span>
            <span className="profile__notif-desc">Stays in the tray at sign-in instead of opening a window.</span>
          </span>
        </label>
      )}

      <UpdateCheck />
    </section>
  );
}

/**
 * Updates, offered rather than applied.
 *
 * Checked once when the screen opens, and installed only when asked: an app
 * that restarts itself has picked the moment on the user's behalf, and in a
 * chat client that moment is reliably mid-sentence.
 */
function UpdateCheck() {
  const { toast } = useToast();
  const [state, setState] = useState<"checking" | "current" | "available" | "failed">("checking");
  const [update, setUpdate] = useState<AvailableUpdate | null>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    checkForUpdate()
      .then((found) => {
        if (cancelled) return;
        setUpdate(found);
        setState(found ? "available" : "current");
      })
      // Being unable to check is not the same as being up to date, and saying
      // the latter when the former happened is the misleading answer.
      .catch(() => !cancelled && setState("failed"));
    return () => {
      cancelled = true;
    };
  }, []);

  const install = useCallback(() => {
    setInstalling(true);
    installUpdate().catch(() => {
      toast("Could not install the update.", { tone: "danger" });
      setInstalling(false);
    });
  }, [toast]);

  return (
    <div className="profile__update-row">
      {state === "checking" && <span className="profile__notif-desc">Checking for updates…</span>}
      {state === "current" && <span className="profile__notif-desc">Astrum Connect is up to date.</span>}
      {state === "failed" && (
        <span className="profile__notif-desc">Could not check for updates just now.</span>
      )}
      {state === "available" && update && (
        <>
          <span className="profile__notif-label">Version {update.version} is available.</span>
          <Button size="sm" onClick={install} loading={installing}>
            Update and restart
          </Button>
        </>
      )}
    </div>
  );
}

/* ─────────────────────── notification preferences ─────────────────────── */

type ResolvedCategory = {
  category: NotificationCategory;
  workspaceEnabled: ChannelPreference;
  userPreference: ChannelPreference;
  effective: ChannelPreference;
};

/**
 * What this person gets told, and how — within whatever an administrator has
 * left switched on workspace-wide.
 *
 * A category the workspace has turned off shows as a disabled row rather
 * than being hidden: hiding it would make "why don't I get task emails"
 * unanswerable from this screen alone, when the true answer is "nobody does,
 * an administrator turned that off" rather than anything this person did.
 */
/*
 * Every zone `Intl` knows about, rather than a hand-picked shortlist — a
 * shortlist is always missing somebody's, and the failure mode (a hidden
 * complaint that mornings arrive at the wrong time) is worse than a long
 * dropdown. `supportedValuesOf` is why this needs no data file of its own.
 */
const TIMEZONES: string[] =
  typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["UTC"];

function PhoneCard() {
  const { viewer, refresh } = useAuth();
  const { toast } = useToast();
  const [value, setValue] = useState(viewer?.phone ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(viewer?.phone ?? ""), [viewer?.phone]);

  if (!viewer) return null;
  const dirty = value.trim() !== (viewer.phone ?? "");

  const save = () => {
    setSaving(true);
    api
      .put("/auth/profile", { phone: value.trim() })
      .then(() => {
        toast(value.trim() ? "Phone number saved." : "Phone number removed.", { tone: "ok" });
        return refresh();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not save your phone number.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  };

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="chat" size={15} />
          Phone
        </h2>
      </div>
      <p className="profile__muted">Shown on your profile to people in your office.</p>
      <div className="profile__card-actions">
        <Input
          aria-label="Phone number"
          type="tel"
          inputMode="tel"
          maxLength={40}
          placeholder="+27 21 555 0100"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <Button loading={saving} disabled={!dirty} onClick={save}>
          Save
        </Button>
      </div>
    </section>
  );
}

function TimezoneCard() {
  const { viewer, refresh } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  if (!viewer) return null;

  /*
   * The browser's own zone is offered as the value only when the account is
   * still on the untouched "UTC" default — someone who has deliberately set
   * UTC themselves must not have it silently replaced.
   */
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const value = viewer.timezone ?? "UTC";
  const isUnsetDefault = value === "UTC" && browserZone !== "UTC";

  const save = (timezone: string) => {
    setSaving(true);
    api
      .put<{ timezone: string }>("/auth/profile", { timezone })
      .then(() => {
        toast("Timezone updated.", { tone: "ok" });
        return refresh();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not update your timezone.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  };

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="clock" size={15} />
          Timezone
        </h2>
      </div>
      <p className="profile__muted">
        Decides when your morning digest and due-task reminders arrive — at 7am in this zone, not the server's.
      </p>
      <div className="profile__card-actions">
        <Select
          aria-label="Timezone"
          value={value}
          disabled={saving}
          onChange={(event) => save(event.target.value)}
        >
          {isUnsetDefault && <option value="UTC">UTC (not yet set — pick your own below)</option>}
          {TIMEZONES.map((zone) => (
            <option key={zone} value={zone}>
              {zone.replace(/_/g, " ")}
            </option>
          ))}
        </Select>
        {isUnsetDefault && (
          <Button variant="secondary" disabled={saving} onClick={() => save(browserZone)}>
            Use {browserZone.replace(/_/g, " ")}
          </Button>
        )}
      </div>
    </section>
  );
}

/**
 * The personal calendar feed: one private URL that Outlook or Google Calendar
 * subscribes to, so events and due tasks show up there without anyone exporting
 * anything.
 *
 * Only a hash of the token is stored, so the address can be shown exactly once,
 * when it is made. Losing it means making a new one, which also kills the old
 * one — that is the same action as revoking, and the copy says so.
 */
function CalendarFeedCard() {
  const { toast } = useToast();
  const [status, setStatus] = useState<{ active: boolean; createdAt: string | null } | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ active: boolean; createdAt: string | null }>("/me/calendar-feed")
      .then((res) => {
        if (!cancelled) setStatus(res);
      })
      .catch(() => {
        if (!cancelled) setStatus({ active: false, createdAt: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const generate = () => {
    setBusy(true);
    api
      .post<{ url: string; active: boolean; createdAt: string | null }>("/me/calendar-feed")
      .then((res) => {
        setUrl(res.url);
        setStatus({ active: res.active, createdAt: res.createdAt });
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not create your calendar link.", { tone: "danger" });
      })
      .finally(() => setBusy(false));
  };

  const revoke = () => {
    setBusy(true);
    api
      .delete("/me/calendar-feed")
      .then(() => {
        setUrl(null);
        setStatus({ active: false, createdAt: null });
        setConfirmRevoke(false);
        toast("Calendar link turned off.", { tone: "ok" });
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not turn off your calendar link.", { tone: "danger" });
      })
      .finally(() => setBusy(false));
  };

  const copy = () => {
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => toast("Link copied.", { tone: "ok" }))
      .catch(() => toast("Could not copy. Select the link and copy it by hand.", { tone: "danger" }));
  };

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="calendar" size={15} />
          Calendar subscription
        </h2>
        {status?.active && <Pill tone="ok">On</Pill>}
      </div>
      <p className="profile__muted">
        Subscribe in Outlook or Google Calendar to see your events and the due dates of your open tasks alongside
        everything else. The link is private to you: anyone who has it can read your schedule, so do not share it.
      </p>

      {url && (
        <div className="profile__feed">
          <Input aria-label="Calendar subscription link" readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
          <Button variant="secondary" icon={<Icon name="copy" size={14} />} onClick={copy}>
            Copy
          </Button>
        </div>
      )}
      {url && (
        <p className="profile__muted">This link is shown only now. Copy it before leaving the page; if it is lost, make a new one.</p>
      )}

      {status?.active && !url && (
        <p className="profile__muted">
          A link is active. For safety it cannot be shown again. Making a new link replaces it and stops the old one
          working.
        </p>
      )}

      <div className="profile__card-actions">
        <Button variant={status?.active ? "secondary" : "primary"} loading={busy} disabled={!status} onClick={generate}>
          {status?.active ? "Make a new link" : "Create calendar link"}
        </Button>
        {status?.active && (
          <Button variant="danger" disabled={busy} onClick={() => setConfirmRevoke(true)}>
            Turn off
          </Button>
        )}
      </div>

      {(url || status?.active) && (
        <details className="profile__howto">
          <summary>How to subscribe</summary>
          <ol>
            <li>
              <strong>Outlook:</strong> Calendar, Add calendar, Subscribe from web. Paste the link, name it, and save.
            </li>
            <li>
              <strong>Google Calendar:</strong> Other calendars (+), From URL. Paste the link and choose Add calendar.
            </li>
          </ol>
          <p className="profile__muted">Both refresh on their own schedule, usually within a few hours, not instantly.</p>
        </details>
      )}

      <Modal open={confirmRevoke} onClose={() => setConfirmRevoke(false)} title="Turn off calendar link?">
        <p className="profile__muted">
          Outlook and Google will stop receiving updates, and the subscribed calendar will go empty there. You can make a new
          link at any time.
        </p>
        <div className="profile__card-actions">
          <Button variant="secondary" onClick={() => setConfirmRevoke(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} onClick={revoke}>
            Turn off
          </Button>
        </div>
      </Modal>
    </section>
  );
}

/**
 * Which office to open into, for someone who belongs to more than one.
 *
 * Renders nothing for a single-office person — they always land in their one
 * office, so there is nothing to choose. The choice is stored on the account
 * (not this browser), so it follows the person to every device; the switcher in
 * the rail still lets them move between offices within a session.
 */
function DefaultOfficeCard() {
  const { viewer, refresh } = useAuth();
  const { workspaces } = useWorkspace();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  // Nothing to choose with a single office. Also guards the pre-load state,
  // where the list is briefly empty.
  if (!viewer || workspaces.length <= 1) return null;

  // Empty string is the "use the standard default office" choice, mapped to
  // null on save. A stored id the viewer no longer belongs to falls through to
  // this too, so the control never shows a dangling selection.
  const known = new Set(workspaces.map((w) => w.id));
  const value = viewer.defaultWorkspaceId && known.has(viewer.defaultWorkspaceId) ? viewer.defaultWorkspaceId : "";

  const save = (next: string) => {
    setSaving(true);
    api
      .put<{ defaultWorkspaceId: string | null }>("/auth/profile", { defaultWorkspaceId: next === "" ? null : next })
      .then(() => {
        toast("Default office updated.", { tone: "ok" });
        return refresh();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not update your default office.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  };

  const defaultOffice = workspaces.find((w) => w.isDefault);

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="home" size={15} />
          Default office
        </h2>
      </div>
      <p className="profile__muted">
        The office you open into when you sign in. You can still switch offices any time from the sidebar.
      </p>
      <div className="profile__card-actions">
        <Select aria-label="Default office" value={value} disabled={saving} onChange={(event) => save(event.target.value)}>
          <option value="">
            {defaultOffice ? `Standard default (${defaultOffice.name})` : "Standard default"}
          </option>
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </Select>
      </div>
    </section>
  );
}

function NotificationPreferences() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<ResolvedCategory[] | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ categories: ResolvedCategory[] }>("/me/notification-preferences")
      .then((res) => setCategories(res.categories))
      .catch(() => toast("Could not load notification settings.", { tone: "danger" }))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const setChannel = useCallback(
    (category: NotificationCategory, channel: "inApp" | "email", value: boolean) => {
      if (!categories) return;
      const current = categories.find((row) => row.category === category);
      if (!current) return;
      const nextPreference: ChannelPreference = { ...current.userPreference, [channel]: value };

      setCategories(
        categories.map((row) =>
          row.category === category
            ? {
                ...row,
                userPreference: nextPreference,
                effective: { ...row.effective, [channel]: row.workspaceEnabled[channel] && value },
              }
            : row,
        ),
      );

      setSaving(true);
      api
        .put<{ categories: ResolvedCategory[] }>("/me/notification-preferences", { [category]: nextPreference })
        .then((res) => setCategories(res.categories))
        .catch(() => {
          toast("Could not save that change.", { tone: "danger" });
          load();
        })
        .finally(() => setSaving(false));
    },
    [categories, load, toast],
  );

  if (loading) {
    return (
      <section className="profile__card">
        <SkeletonText lines={3} />
      </section>
    );
  }
  if (!categories) return null;

  return (
    <section className="profile__card" id="notifications">
      <div className="profile__card-head">
        <h2>
          <Icon name="bell" size={15} />
          Notifications
        </h2>
        {saving && <Pill tone="neutral">Saving…</Pill>}
      </div>
      <p className="profile__muted">
        What you are told about, and how. A row an administrator has turned off workspace-wide is disabled here —
        nobody gets that one, not just you.
      </p>

      <div className="profile__notif-grid" role="table" aria-label="Notification preferences">
        <div className="profile__notif-row profile__notif-row--head" role="row">
          <span role="columnheader">Category</span>
          <span role="columnheader">In-app</span>
          <span role="columnheader">Email</span>
        </div>
        {NOTIFICATION_CATEGORIES.map((category) => {
          const row = categories.find((r) => r.category === category);
          if (!row) return null;
          return (
            <div key={category} className="profile__notif-row" role="row">
              <div role="cell">
                <span className="profile__notif-label">{NOTIFICATION_CATEGORY_LABEL[category]}</span>
                <span className="profile__notif-desc">{NOTIFICATION_CATEGORY_DESCRIPTION[category]}</span>
              </div>
              <div role="cell">
                <Checkbox
                  aria-label={`${NOTIFICATION_CATEGORY_LABEL[category]} — in-app`}
                  checked={row.userPreference.inApp}
                  disabled={!row.workspaceEnabled.inApp}
                  title={!row.workspaceEnabled.inApp ? "Turned off workspace-wide by an administrator." : undefined}
                  onChange={(e) => setChannel(category, "inApp", e.target.checked)}
                />
              </div>
              <div role="cell">
                <Checkbox
                  aria-label={`${NOTIFICATION_CATEGORY_LABEL[category]} — email`}
                  checked={row.userPreference.email}
                  disabled={!row.workspaceEnabled.email}
                  title={!row.workspaceEnabled.email ? "Turned off workspace-wide by an administrator." : undefined}
                  onChange={(e) => setChannel(category, "email", e.target.checked)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────────── main view ─────────────────────────────── */

export function Profile() {
  usePageTitle("Profile");
  const { viewer, signOut, refresh } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  /*
   * Push is per browser, not per account, so this is read from this browser
   * rather than from the viewer. Starts as "unconfigured" so the card is hidden
   * until the check completes — briefly showing a control and then removing it
   * is worse than showing it a moment late.
   */
  const [push, setPush] = useState<PushState>("unconfigured");
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    void pushState().then(setPush);
  }, []);

  const togglePush = useCallback(async () => {
    setPushBusy(true);
    try {
      const next = push === "on" ? await disablePush() : await enablePush();
      setPush(next);
      if (next === "denied") {
        toast("Your browser blocked notifications. Allow them in its site settings.", {
          tone: "danger",
        });
      } else if (next === "on") {
        toast("Notifications are on for this browser.", { tone: "ok" });
      }
    } catch {
      toast("Could not change notification settings.", { tone: "danger" });
    } finally {
      setPushBusy(false);
    }
  }, [push, toast]);

  const [twoFactorOn, setTwoFactorOn] = useState(!!viewer?.twoFactorEnabled);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [signOutAllOpen, setSignOutAllOpen] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  const disableTwoFactor = useCallback(() => {
    if (disableCode.length !== 6) return;
    setDisabling(true);
    api
      .post("/auth/two-factor/disable", { code: disableCode })
      .then(() => {
        setTwoFactorOn(false);
        setDisableOpen(false);
        setDisableCode("");
        toast("Two-factor authentication is off.");
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "That code was not accepted.", { tone: "danger" });
      })
      .finally(() => setDisabling(false));
  }, [disableCode, toast]);

  const signOutEverywhere = useCallback(() => {
    setSigningOutAll(true);
    api
      .post("/auth/logout-all")
      .then(() => {
        // Every session is gone, including this one — so the only correct next
        // step is back to the sign-in screen.
        toast("Signed out on every device.");
        void signOut();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not sign out everywhere.", { tone: "danger" });
        setSigningOutAll(false);
        setSignOutAllOpen(false);
      });
  }, [signOut, toast]);

  if (!viewer) return null;

  return (
    <>
      <PageBody
      className="profile"
      width="narrow"
      title={viewer.displayName}
      /* An avatar beside the name rather than a plain title — this page is
         about a person, and the face is the heading. */
      heading={
        <div className="profile__header">
          <AvatarUpload name={viewer.displayName} avatarUrl={viewer.avatarUrl} onChanged={refresh} />
          <div className="profile__identity">
            <NameEditor name={viewer.displayName} onChanged={() => void refresh()} />
            <p>{viewer.email}</p>
            <Pill tone={viewer.role === "admin" ? "accent" : "neutral"}>
              {ROLE_LABEL[viewer.role] ?? viewer.role}
            </Pill>
          </div>
        </div>
      }
    >

        <section className="profile__card">
          <div className="profile__card-head">
            <h2>
              <Icon name="lock" size={15} />
              Two-factor authentication
            </h2>
            {twoFactorOn && <Pill tone="ok">On</Pill>}
          </div>
          <p className="profile__muted">
            {twoFactorOn
              ? "Signing in asks for a code from your authenticator as well as your password."
              : "Add a second step to signing in, so a stolen password is not enough on its own."}
          </p>
          <div className="profile__card-actions">
            {twoFactorOn ? (
              <Button variant="danger" onClick={() => setDisableOpen(true)}>
                Turn off
              </Button>
            ) : (
              <Button onClick={() => setSetupOpen(true)}>Set up two-factor</Button>
            )}
          </div>
        </section>

        <section className="profile__card">
          <div className="profile__card-head">
            <h2>
              <Icon name="settings" size={15} />
              Sessions
            </h2>
          </div>
          <p className="profile__muted">
            Signing out everywhere ends every session on every device, including this one. Do this if you have lost a
            device or think someone else has your password.
          </p>
          <div className="profile__card-actions">
            <Button variant="secondary" onClick={() => void signOut()}>
              Sign out
            </Button>
            <Button variant="danger" onClick={() => setSignOutAllOpen(true)}>
              Sign out everywhere
            </Button>
          </div>
        </section>

        <section className="profile__card">
          <div className="profile__card-head">
            <h2>
              <Icon name="lock" size={15} />
              Password
            </h2>
          </div>
          <p className="profile__muted">
            Changing it signs out every other device but keeps you signed in here. You will need your current password.
          </p>
          <div className="profile__card-actions">
            <Button variant="secondary" onClick={() => navigate("/change-password")}>
              Change password
            </Button>
          </div>
        </section>

        {/*
          * Hidden entirely when the deployment has no VAPID keys, rather than
          * shown disabled: a control that cannot work is worse than one that is
          * absent, and this one would ask the browser for a permission the
          * server could not then act on.
          */}
        {/*
         * Push here means the browser's Push API — a mechanism the desktop
         * build does not use at all, since its webview has no PushManager and
         * popups instead go through the OS notification centre (see
         * NativeNotificationWarning, which watches for that failing).
         * Showing this card on desktop reported "This browser cannot show
         * desktop notifications. Chrome, Edge and Firefox can" — read on the
         * desktop app itself, that sentence describes the wrong problem
         * entirely and sends people looking for a browser setting that
         * doesn't exist there.
         */}
        {!isDesktop && !isNativeApp && push !== "unconfigured" && (
          <section className="profile__card">
            <div className="profile__card-head">
              <h2>
                <Icon name="bell" size={15} />
                Desktop notifications
              </h2>
            </div>
            <p className="profile__muted">
              {push === "unsupported"
                ? "This browser cannot show desktop notifications. Chrome, Edge and Firefox can; Safari needs the site added to your Dock first."
                : push === "denied"
                  ? "Notifications are blocked for this site. Your browser will not ask again — allow them in its site settings, then reload this page. On Edge or Chrome, click the icon left of the address bar to change it. On Windows, also check Settings → System → Notifications hasn't turned off notifications for your browser entirely."
                  : push === "on"
                    ? "On for this browser. You will be notified even when the tab is closed. This is set per browser, so another computer needs turning on separately."
                    : "Be told when someone messages you or assigns you work, even when the tab is closed. Set per browser, so each computer you use needs its own."}
            </p>
            {(push === "on" || push === "off") && (
              <div className="profile__card-actions">
                <Button
                  variant={push === "on" ? "secondary" : "primary"}
                  loading={pushBusy}
                  onClick={() => void togglePush()}
                >
                  {push === "on" ? "Turn off" : "Turn on notifications"}
                </Button>
              </div>
            )}
          </section>
        )}

        {isDesktop && <DesktopNotificationSettings />}
        {isNativeApp && <PhoneNotificationSettings />}

        <StatusSettings />
        <TimezoneCard />
        <PhoneCard />

        <CalendarFeedCard />
        <DefaultOfficeCard />
        <NotificationPreferences />
        {isDesktop && <DesktopSettings />}
        <WhatsNewCard />
        <AboutCard />
      </PageBody>

      <Modal open={setupOpen} onClose={() => setSetupOpen(false)} title="Set up two-factor authentication">
        <TwoFactorSetup onEnabled={() => setTwoFactorOn(true)} onClose={() => setSetupOpen(false)} />
      </Modal>

      <Modal open={disableOpen} onClose={() => setDisableOpen(false)} title="Turn off two-factor?">
        <div className="profile__dialog">
          <p className="profile__muted">
            Enter a current code to confirm. Your account will be protected by its password alone afterwards.
          </p>
          <Input
            label="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            autoFocus
          />
          <div className="profile__dialog-actions">
            <Button variant="ghost" onClick={() => setDisableOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={disabling} disabled={disableCode.length !== 6} onClick={disableTwoFactor}>
              Turn off
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={signOutAllOpen} onClose={() => setSignOutAllOpen(false)} title="Sign out everywhere?">
        <div className="profile__dialog">
          <p className="profile__muted">
            Every device signed in as <strong>{viewer.email}</strong> will be signed out, including this one. You will
            need to sign in again.
          </p>
          <div className="profile__dialog-actions">
            <Button variant="ghost" onClick={() => setSignOutAllOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={signingOutAll} onClick={signOutEverywhere}>
              Sign out everywhere
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
